<template>
  <div>
    <base-dialog :active="displayLoginDialog" :title="t('savedialog.Save wheel')" :cancel-text="t('common.Cancel')"
      @close="enter_inactive" @cancel="enter_inactive">
      <template #title>
        <div class="flex items-center">
          <i class="fa fa-save mr-2"></i>{{ t('savedialog.Save wheel') }}
        </div>
      </template>

      <div class="p-4 space-y-4">
        <p>
          {{ t('savedialog.To save wheels') }}
        </p>
        <div class="rounded-lg border border-stone-200 dark:border-stone-700 bg-white/80 dark:bg-stone-800/60 p-4">
          <div class="space-y-3">
            <button class="btn btn-primary w-full flex items-center justify-center gap-2" @click="continueWithGoogle"
              :disabled="loading">
              <i class="fab fa-google"></i>
              <span>{{ t('common.Sign in with Google') }}</span>
            </button>

            <div class="relative flex items-center">
              <div class="flex-grow border-t border-stone-200 dark:border-stone-700"></div>
              <span class="px-3 text-xs text-stone-500 dark:text-stone-400">{{ t('common.or') }}</span>
              <div class="flex-grow border-t border-stone-200 dark:border-stone-700"></div>
            </div>

            <form @submit.prevent="onSubmit" class="space-y-2">
              <input v-model="email" type="email" required :placeholder="t('common.Email') || 'Email'"
                class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
              <input v-model="password" type="password" required :placeholder="t('common.Password') || 'Password'"
                class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
              <div v-if="authMode === 'signUp'">
                <input ref="displayNameInput" v-model="displayName" type="text"
                  :aria-invalid="showDisplayNameError ? 'true' : 'false'" required
                  :placeholder="t('common.Display name') || 'Display name'"
                  class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
                <p v-if="showDisplayNameError" class="mt-1 text-xs text-red-600 dark:text-red-300">
                  {{ t('common.Display name is required') }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <button v-if="authMode === 'signIn'" type="submit" class="btn btn-primary" :disabled="loading">{{
                  t('common.Sign in') }}</button>
                <button v-if="authMode === 'signIn'" type="button"
                  class="ml-auto text-primary-600 hover:text-primary-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm"
                  @click="signUpEmail" :disabled="loading">
                  {{ t('common.Create account') }}
                </button>
                <template v-else>
                  <button type="submit" class="btn btn-primary" :disabled="loading">{{ t('common.Create account')
                  }}</button>
                  <button type="button"
                    class="ml-auto text-primary-600 hover:text-primary-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm"
                    @click="authMode = 'signIn'" :disabled="loading">
                    {{ t('common.Sign in instead') || 'Sign in instead' }}
                  </button>
                </template>
              </div>
            </form>
          </div>
        </div>
      </div>
    </base-dialog>

    <base-dialog :active="displaySaveDialog" :title="t('savedialog.Save wheel')" :cancel-text="t('common.Cancel')"
      :confirm-text="t('common.Save')" :confirm-disabled="!saveAsNameIsValid" @close="enter_inactive"
      @cancel="enter_inactive" @confirm="enter_savingWheel">
      <template #title>
        <div class="flex items-center">
          <i class="fa fa-save mr-2"></i>{{ t('savedialog.Save wheel') }}
        </div>
      </template>
      <template #header-actions>
        <button @click="openAccountFromHere" class="flex items-center" aria-label="Open account">
          <img :src="userPhotoUrl" alt="User avatar"
            class="w-8 h-8 rounded-full ring-2 ring-stone-200 dark:ring-stone-700 shadow-sm hover:ring-primary-500 transition-all">
        </button>
      </template>

      <div class="p-4 space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div>
            {{ t('savedialog.Save as') }}
          </div>
          <div class="md:col-span-2">
            <input type="text" v-model="saveAsName" @keyup.enter="enter_savingWheel" ref="saveAsField" required
              maxlength="50" class="form-input w-full">
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div>
            {{ t('savedialog.Your existing wheels') }}
          </div>
          <div class="md:col-span-2">
            <select v-model="existingWheelTitle" class="form-select w-full">
              <option value="" disabled selected>{{ t('savedialog.Select a wheel') }}</option>
              <option v-for="wheel in validSavedWheels" :value="wheel.title" :key="wheel.title">
                {{ wheel.title }}
              </option>
            </select>
          </div>
        </div>

        <p class="text-stone-600 dark:text-stone-400 mt-4">
          {{ t('savedialog.You will always be able to access') }}
        </p>
      </div>
    </base-dialog>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, getCurrentInstance, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWheelStore } from '@/stores/useWheelStore';
import { useUserStore } from '@/stores/useUserStore';
import * as Util from '@utils/Util.js';
import BaseDialog from '../shared/BaseDialog.vue';
import { onOpen as onDialogOpen } from '@/services/ui/DialogCoordinator.js';
import useAuth from '@/composables/useAuth.js';

const { t } = useI18n();
const wheelStore = useWheelStore();
const userStore = useUserStore();
const auth = useAuth();

const emit = defineEmits(['start-wait-animation', 'stop-wait-animation', 'show-snackbar-message', 'reset-address-bar', 'open-account-dialog']);

const fsm = ref('inactive');
const loading = ref(false);
const email = ref('');
const password = ref('');
const displayName = ref('');
const authMode = ref('signIn');
const showDisplayNameError = ref(false);
const displayNameInput = ref(null);
const saveAsName = ref('');
const existingWheelTitle = ref(null);
const saveAsField = ref(null);
const userPhotoUrl = computed(() => userStore.getUserPhotoUrl || '/images/user_profile.png');

const savedWheels = computed(() => userStore.savedWheels);
const isLoggedIn = computed(() => userStore.isUserLoggedIn);
const wheelConfig = computed(() => wheelStore.wheelConfig);
const wheelTitle = computed(() => wheelStore.wheelTitle);

const saveAsNameIsValid = computed(() => saveAsName.value.length > 0);

const displayLoginDialog = computed({
  get: () => fsm.value === 'userIsPickingLoginMethod',
  set: (val) => { if (val === false) fsm.value = 'inactive'; }
});

const displaySaveDialog = computed({
  get: () => fsm.value === 'userIsEnteringName',
  set: (val) => { if (val === false) fsm.value = 'inactive'; }
});

// Ensure this dialog closes if another dialog is opened
let unsubscribeDialogOpen = null;
onMounted(() => {
  unsubscribeDialogOpen = onDialogOpen((name) => {
    if (name !== 'save') {
      fsm.value = 'inactive';
    }
  });
});
onBeforeUnmount(() => {
  if (typeof unsubscribeDialogOpen === 'function') {
    try { unsubscribeDialogOpen(); } catch { }
  }
});

const validSavedWheels = computed(() => {
  const wheels = Array.isArray(savedWheels.value) ? savedWheels.value : [];
  return wheels.filter(w => w && w.title && w.title.trim().length > 0);
});

// If user logs in while dialog is open, refresh the list
watch(isLoggedIn, (now) => {
  if (now && fsm.value !== 'inactive') {
    userStore.loadSavedWheels().catch(() => { });
  }
});

watch(existingWheelTitle, (newValue) => {
  saveAsName.value = newValue || '';
});

// Clear display-name error as the user types a valid name
watch(displayName, (val) => {
  if (showDisplayNameError.value && val && val.trim()) {
    showDisplayNameError.value = false;
  }
});

const show = async () => {
  if (wheelConfig.value.isTooBigForDatabase()) {
    Util.trackEvent('Wheel', 'WheelTooBigForDatabase', '');
    alert(t('savedialog.Sorry, too many images'));
    return;
  }
  saveAsName.value = getWheelTitle();
  enter_loadingLibraries();
};

const getWheelTitle = () => {
  const title = wheelTitle.value || wheelConfig.value.title || '';
  return title.trim() || 'My wheel';
};

const enter_loadingLibraries = async () => {
  fsm.value = 'loadingLibraries';
  try {
    emit('start-wait-animation');
    const userIsLoggedIn = await userStore.userIsLoggedInAction();
    if (userIsLoggedIn) {
      enter_loadingWheels();
    } else {
      enter_userIsPickingLoginMethod();
    }
  } catch (ex) {
    enter_authError(ex);
  } finally {
    emit('stop-wait-animation');
  }
};

const enter_inactive = () => {
  fsm.value = 'inactive';
  resetDialogState();
};

const resetDialogState = () => {
  existingWheelTitle.value = null;
  saveAsName.value = '';
  nextTick(() => {
    const el = saveAsField.value;
    if (el && el.blur) el.blur();
  });
};

const enter_userIsPickingLoginMethod = async () => {
  fsm.value = 'userIsPickingLoginMethod';
  // Await user action for Google redirect or Email form
  authMode.value = 'signIn';
  displayName.value = '';
  showDisplayNameError.value = false;
};

const enter_loadingWheels = async () => {
  fsm.value = 'loadingWheels';
  try {
    await userStore.loadSavedWheels();
  } catch { }
  existingWheelTitle.value = null;
  saveAsName.value = getWheelTitle();
  setTimeout(() => { if (saveAsField.value && saveAsField.value.focus) saveAsField.value.focus(); }, 100);
  enter_userIsEnteringName();
};

const enter_userIsEnteringName = () => { fsm.value = 'userIsEnteringName'; };

const enter_savingWheel = async () => {
  fsm.value = 'savingWheel';
  const trimmedTitle = saveAsName.value.trim();
  if (!trimmedTitle) {
    console.error('Cannot save wheel: Empty title');
    saveAsName.value = 'My wheel';
    return;
  }
  wheelStore.setWheelTitle(trimmedTitle);
  try {
    emit('start-wait-animation');
    await userStore.saveWheel(wheelConfig.value);
    emit('stop-wait-animation');
    const message = t('savedialog.Wheel saved successfully', { wheelTitle: Util.removeHtml(trimmedTitle) });
    emit('show-snackbar-message', message);
    emit('reset-address-bar');
    enter_inactive();
  } catch (ex) {
    emit('stop-wait-animation');
    Util.trackException(ex);
    enter_authError(ex);
  }
};

const enter_authError = (exception) => {
  fsm.value = 'authError';
  userStore.logOut();
  enter_inactive();
};

defineExpose({ show, enter_inactive });

function openAccountFromHere() {
  enter_inactive();
  emit('open-account-dialog');
}

// New auth helpers
const continueWithGoogle = async () => {
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForSaveAttempt', 'google');
    const resultUser = await auth.signInWithGoogle();
    if (resultUser) {
      await auth.postSignInSetup();
      enter_loadingWheels();
    }
  } catch (ex) {
    Util.trackEvent('Wheel', 'LoginForSaveFailure', 'google');
    enter_authError(ex);
  } finally {
    loading.value = false;
  }
};

const signInEmail = async () => {
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForSaveAttempt', 'email');
    await auth.signInWithEmailPassword(email.value.trim(), password.value);
    Util.trackEvent('Wheel', 'LoginForSaveSuccess', 'email');
    await auth.postSignInSetup();
    enter_loadingWheels();
  } catch (ex) {
    Util.trackEvent('Wheel', 'LoginForSaveFailure', 'email');
    if (ex && (ex.code === 'auth/user-not-found' || ex.code === 'auth/invalid-credential')) {
      authMode.value = 'signUp';
      showDisplayNameError.value = false;
      nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
    } else {
      enter_authError(ex);
    }
  } finally {
    loading.value = false;
  }
};

const signUpEmail = async () => {
  if (authMode.value === 'signIn') {
    authMode.value = 'signUp';
    showDisplayNameError.value = false;
    nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
    return;
  }
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForSaveAttempt', 'email-signup');
    if (!displayName.value || !displayName.value.trim()) {
      showDisplayNameError.value = true;
      loading.value = false;
      nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
      return;
    }
    await auth.registerWithEmailPassword(
      email.value.trim(),
      password.value,
      displayName.value.trim()
    );
    Util.trackEvent('Wheel', 'LoginForSaveSuccess', 'email-signup');
    await auth.postSignInSetup();
    enter_loadingWheels();
  } catch (ex) {
    Util.trackEvent('Wheel', 'LoginForSaveFailure', 'email-signup');
    enter_authError(ex);
  } finally {
    loading.value = false;
  }
};

const onSubmit = async () => {
  if (authMode.value === 'signIn') {
    await signInEmail();
  } else {
    await signUpEmail();
  }
};
</script>
